import {
    IChapter,
    INamedEntitySummary,
    INoteInsight,
    MedicalNoteType,
} from '../../types';

export enum ProductAnalyticsSummaryType {
    EntitySummary = 'EntitySummary',
    MedicalNote = 'MedicalNote',
    ChapterGist = 'ChapterGist',
    ChapterHeadline = 'ChapterHeadline',
    ChapterSnippet = 'ChapterSnippet',
}

export type SummaryContextData =
    | {
          summaryType: ProductAnalyticsSummaryType.EntitySummary;
          namedEntity: INamedEntitySummary;
      }
    | {
          summaryType: ProductAnalyticsSummaryType.MedicalNote;
          noteType: MedicalNoteType;
          noteCategory: string;
          noteInsight: INoteInsight;
      }
    | {
          summaryType:
              | ProductAnalyticsSummaryType.ChapterGist
              | ProductAnalyticsSummaryType.ChapterHeadline
              | ProductAnalyticsSummaryType.ChapterSnippet;
          chapter: IChapter;
          segmentId: string;
      };

/* Used with location "Sign Up" */
type SignUpUiEventName =
    | 'Verify email code'
    | 'Create Account'
    | 'Add Password'
    | 'Send verification code'
    | 'Resend Code'
    | 'Change Phone Number'
    | '2FA Code inserted'
    | 'Practice Setup Confirm'
    | 'Sign up Questionnaire Submitted'
    | 'Sign up Experience Selected'
    // Here starts user onboarding
    | 'Welcome Continue'
    | 'Profile Setup Confirm'
    | 'Client Sign Up' // deprecated soon
    | 'Org Workflow Setup Confirm'
    | 'Invite Team Members Confirm'
    | 'Invite Team Members Skip'
    | 'Bulk Invite Members Dialog'
    | 'Finalization Continue'
    | 'Onboarding Completed';

export type UiEventName =
    // Tracking protocol: https://docs.google.com/spreadsheets/d/19_lngWdOWI0DQxgYfis_IMBMswG7WzFPpPUUUltrtxg/edit?pli=1#gid=1480549838
    | 'Feature Flag Enabled'
    | 'Unsupported Browser'
    | 'Continue Anyway'
    | 'Join the Waitlist'
    | 'Change Password'
    | 'Forgot Password'
    | 'Toggle Remember Me'
    | 'Sign In'
    | 'Sign Up'
    | 'Sign in with Google'
    | 'Browser Incompatible With Virtual Session'
    | 'System Incompatible With Desktop Virtual Session'
    | 'Mobile Incompatible With Virtual Session'
    | 'Download Chrome'
    | 'Download Edge'
    | 'Switch to Password Sign In'
    | 'Switch to SSO Sign In'
    | 'Sign in Error'
    | 'Send Forgot Password Email'
    | 'Send Forgot Password Email Error'
    | 'Go Home'
    | 'Open Settings'
    | 'Open Referral Program'
    | 'Open AI Client Session'
    | 'Close AI Client Session'
    | 'Try Later AI Client Session'
    | 'Start AI Client Session'
    | 'Close AI Client Promo'
    | 'Change Settings Menu Item'
    | 'Open Account Settings'
    | 'Open Account Menu'
    | 'Open Support'
    | 'Get Help'
    | 'Open Feature Request'
    | 'Open Roadmap'
    | 'Open Recent Updates'
    | 'Logout'
    | 'Open Client Picker'
    | 'Open Full Text Search'
    | 'Close Full Text Search'
    | 'Clear Full Text Search'
    | 'Execute Full Text Search'
    | 'Select Full Text Search Result'
    | 'Select Full Text Search Client Result'
    | 'Open AI Chat From Full Text Search'
    | 'Change Full Text Search Source Filter'
    | 'Change Full Text Search Client Filter'
    | 'Change Full Text Search Date Filter'
    | 'Open Client Detail'
    | 'Open Client Select'
    | 'View Client Detail Sessions'
    | 'View Client Detail TreatmentPlan'
    | 'View Client Detail Treatment Summary'
    | 'View Client Detail Treatment Plan V2'
    | 'View Client Detail Billing'
    | 'View Client Detail Forms'
    | 'View Client Detail Chart Notes'
    | 'View Client Detail Files'
    | 'Change Client Detail Tab'
    | 'Close Client Select'
    | 'Select Client'
    | 'Disable 2FA'
    | 'Enable 2FA'
    | 'Cancel 2FA'
    | 'Open Add New Client Dialog'
    | 'Search Client'
    | 'Open New Session Menu'
    | 'Start Session'
    | 'Start Session Cancel'
    | 'Start Session Confirm'
    | 'Continue Session'
    | 'Conclude Session'
    | 'Conclude Session Confirm'
    | 'Conclude Session Validation Failed'
    | 'Conclude Session Validation Passed'
    | 'Start Online Session'
    | 'Start Audio Recorder'
    | 'Start Virtual Session Recorder'
    | 'Start Virtual Session Recorder Capture'
    | 'Close Audio Recorder'
    | 'Close Virtual Session Recorder'
    | 'Open Virtual Session Mode Switcher'
    | 'Select Virtual Session Mode'
    | 'Close Virtual Session Sharing Guide'
    | 'Upload For Processing'
    | 'Start Extension Recorder'
    | 'Extension Recorder'
    | 'Stop Extension Recorder'
    | 'Download macOS App'
    | 'Download Zoom App'
    | 'Session Capture Navigation'
    | 'Telehealth Navigation'
    | 'Open Book Session Dialog'
    | 'Open Upload Session Dialog'
    | 'Close Upload Session Dialog'
    | 'Open File Picker'
    | 'Close File Picker'
    | 'File Selected'
    | 'File Rejected'
    | 'Edit Session Booking'
    | 'Delete Session Booking'
    | 'Copy Uphealer Link'
    | 'Show Upcoming Sessions'
    | 'Show Completed Sessions'
    | 'Show More Sessions'
    | 'Show More Upcoming Sessions'
    | 'Show More Clients'
    | 'Sort Clients'
    | 'Sort Clients By First Name'
    | 'Sort Clients By Last Name'
    | 'Sort Forms'
    | 'Copy to Clipboard'
    | 'Rate Document'
    | 'Rate Document Confirm'
    | 'Rate Document Cancel'
    | 'Rate Chat'
    | 'Rate Chat Confirm'
    | 'Rate Chat Cancel'
    | 'Rate Editor Segment'
    | 'Rate Editor Segment Confirm'
    | 'Rate Editor Segment Cancel'
    | 'Change Rating'
    | 'Select Issue'
    | 'Unselect Issue'
    | 'Enter Feedback'
    | 'Generate PDF'
    | 'Open Session Detail'
    | 'Open Application Session Detail'
    | 'Rename Session'
    | 'Rename Session Error'
    | 'Edit Session Summary'
    | 'Edit Session Date'
    | 'Session Detail'
    | 'Change Session Detail Tab'
    | 'Single Voice Warning Dialog'
    | 'Single Voice Warning Dialog Opened'
    | 'Single Voice Warning Acknowledged'
    | 'Single Voice Warning Mistake Reported'
    | 'Show Private Notes'
    | 'Show Progress Notes'
    | 'Fold'
    | 'Unfold'
    | 'Select Chapter'
    | 'Go To Transcript Word'
    | 'Play Recording'
    | 'Continue Recording'
    | 'Upload Session'
    | 'Delete Session'
    | 'Delete Session Cancel'
    | 'Delete Session Confirm'
    | 'Delete Recording'
    | 'Delete Recording Cancel'
    | 'Delete Recording Confirm'
    | 'Download Recording'
    | 'Delete Transcript'
    | 'Delete Transcript Cancel'
    | 'Delete Transcript Confirm'
    | 'Open Edit Session Dialog'
    | 'Open Re-assign Session Dialog'
    | 'Re-assign Session Cancel'
    | 'Re-assign Session Confirm'
    | 'Edit Title And Summary Cancel'
    | 'Rename Private Note'
    | 'Rename Note'
    | 'Navigate to Insight'
    | 'Navigate to Transcript'
    | 'Editor Drag Start'
    | 'Editor Drag End'
    | 'Delete Editor Block'
    | 'Add to Notes'
    | 'Return To Current Position'
    | 'Add New Client Dialog'
    | 'Create New Client'
    | 'Create New Client Error'
    | 'Open Edit Client Dialog'
    | 'Edit Client Dialog'
    | 'Edit Client'
    | 'Cancel Client Edit'
    | 'Edit Client Error'
    | 'Remove Client'
    | 'Remove Client Error'
    | 'Open Archive Client Dialog'
    | 'Open Delete Client Dialog'
    | 'Delete Client'
    | 'Cancel Delete Client'
    | 'Add Additional Participant'
    | 'Edit Additional Participants'
    | 'Add Additional Participants'
    | 'Remove Additional Participant'
    | 'Confirm Additional Participants'
    | 'Open Additional Participant Detail'
    | 'Close Additional Participant Detail'
    | 'Update Additional Participant'
    | 'Revert Additional Participant Change'
    | 'Toggle Client Presence'
    | 'Account Settings Dialog'
    | 'Region Changed'
    | 'Update Settings'
    | 'Update Password'
    | 'Update Error'
    | 'Book Session Dialog'
    | 'Book Session'
    | 'Update Session Booking'
    | 'Book Session Error'
    | 'Close Dialog'
    | 'Upload Session Dialog'
    | 'Select File'
    | 'Upload File'
    | 'Upload File Error'
    | 'Save Session'
    | 'Save Session Error'
    | 'Permission Check'
    | 'Request Permissions'
    | 'Try Again'
    | 'View Support Article'
    | 'Hair Check'
    | 'Toggle Camera'
    | 'Open Microphone Select'
    | 'Toggle Microphone'
    | 'Toggle Blur'
    | 'Toggle Reduce Noise'
    | 'Select Device'
    | 'Set Remembered Device'
    | 'Share Session Link'
    | 'Permissions Not Granted'
    | 'Poor Connection Indicator'
    | 'Video Interrupted'
    | 'Retry Audio'
    | 'Retry Audio Failed'
    | 'Retry Audio Success'
    | 'Auto Retry Audio Success'
    | 'Knock'
    | 'Remove Knock'
    | 'Video Call'
    | 'Video Call Landing Page'
    | 'Video Call Provider'
    | 'Video Call Client'
    | 'Provider Selection Dialog'
    | 'Provider Selected'
    | 'Share Screen'
    | 'Stop Share Screen'
    | 'Change AI Assistant State'
    | 'Change AI Notes State'
    | 'Toggle Waiting Room AI Notes'
    | 'Exceeded Max Recording Duration'
    | 'Toggle Full Screen'
    | 'Change View Layout'
    | 'Open Performance Settings Dialog'
    | 'Close Performance Settings Dialog'
    | 'Change Video Quality'
    | 'Toggle Self View'
    | 'Toggle Picture in Picture'
    | 'End Session'
    | 'End Session Cancel'
    | 'End Session Confirm'
    | 'Multiple Participants Warning'
    | 'Session End Dialog'
    | 'Select Existing Client'
    | 'Open Organization Management'
    | 'Reactivate User'
    | 'Reactivate User Cancel'
    | 'Reactivate User Confirm'
    | 'Resend Invitation'
    | 'Deactivate User'
    | 'Deactivate User Cancel'
    | 'Deactivate User Confirm'
    | 'Remove User'
    | 'Reassign All Contracts'
    | 'Reassign All Contracts Cancel'
    | 'Reassign All Contracts Confirm'
    | 'Open Trial Expiration Dialog'
    | 'Close Trial Expiration Dialog'
    | 'Open Pricing Plans Dialog'
    | 'Close Pricing Plans Dialog'
    | 'Switch Billing Period'
    | 'Change Premium Plan'
    | 'Change Plan'
    | 'Activate Premium Plan'
    | 'Learn More Metered Plan'
    | 'Change Plan Seats'
    | 'Switch To Free'
    | 'Switch To Current'
    | 'Contact Us'
    | 'Contact Sales'
    | 'Export Usage'
    | 'Welcome To Premium'
    | 'Welcome To Free'
    | 'Cancel Change Plan'
    | 'Show Plans'
    | 'Show Billing Overview'
    | 'Show Inactive Plan Dialog'
    | 'Show Billing'
    | 'Open Billing FAQ'
    | 'Upgrade Plan'
    | 'Show Upgrade Plan'
    | 'Change Session Type'
    | 'Pricing Card Clicked'
    | 'Show Solo Pricing'
    | 'Show Custom Pricing'
    | 'Compare Plans'
    | 'Show Customer Portal'
    | 'Pause / Cancel Subscription'
    | 'Change Keep Recording Settings'
    | 'Change Note Type'
    | 'Change Default Note Type'
    | 'Change Not Default Note Type'
    | 'Change Note Type Cancel'
    | 'Change Note Type Confirm'
    | 'Change Client Summary Template'
    | 'Discover Calendar Integration'
    | 'Connect Google Calendar'
    | 'Connect Google Calendar Cancel'
    | 'Connect Google Calendar Confirm'
    | 'Disconnect Google Calendar'
    | 'Disconnect Google Calendar Cancel'
    | 'Disconnect Google Calendar Confirm'
    | 'Click Zoom Promo Banner'
    | 'Click Extension Promo Banner'
    | 'Extension Promo Banner'
    | 'Zoom Promo Banner'
    | 'iOS Promo Banner'
    | 'Dismiss Promo Banner'
    | 'Click iOS Recorder Promo Banner'
    | 'Connect Zoom App'
    | 'Connect Zoom App Cancel'
    | 'Connect Zoom App Confirm'
    | 'Disconnect Zoom App'
    | 'Disconnect Zoom App Cancel'
    | 'Disconnect Zoom App Confirm'
    | 'Zoom App Install Page'
    | 'Zoom App Install Failed'
    | 'Zoom App Auth Page'
    | 'Zoom Main Client'
    | 'Zoom App Missing Integration Page'
    | 'Zoom App Missing Integration Resolved'
    | 'Zoom Onboarding'
    | 'Zoom Meeting'
    | 'Extension Install'
    | 'Open Upheal App'
    | 'Stop Recording Prompt'
    | 'Change Client Cancel'
    | 'Change Client Confirm'
    | 'Cloud Recording Event'
    | 'Open Upcoming Session'
    | 'Manage Team Members'
    | 'Add Team Member'
    | 'Add Team Member Confirm'
    | 'Remove Team Member'
    | 'Remove Team Member Confirm'
    | 'Remove Team Member Cancel'
    | 'Switch Team Member'
    | 'Switch Team Member Confirm'
    | 'Switch Team Member Cancel'
    | 'Export Client Data'
    | 'Create Account'
    | 'Resend Code'
    | 'Save Note Settings'
    | 'Update Practice Profile'
    | 'Submit Questionnaire'
    | 'Switch To Sign In'
    | 'Register With Email'
    | 'Data Preferences Update'
    | 'Transcript Deletion Update'
    | 'Missing Client Consent'
    | 'Resolve Consent'
    | 'Ask For Consent'
    | 'Skip Consent'
    | 'Dismiss Consent'
    | 'Accept Consent'
    | 'Decline Consent'
    | 'Accept Store Audio Recording Consent'
    | 'Decline Store Audio Recording Consent'
    | 'Recording Storage Consent Dialog Opened'
    | 'Recording Storage Consent Confirmed'
    | 'Send Email Consent'
    | 'Cancel Send Email Consent'
    | 'Collect Via Email Opened'
    | 'Upload Success'
    | 'Upload Error'
    | 'Upload Aborted'
    | 'Retry Upload'
    | 'Audio Recorder'
    | 'Audio Recorder Error'
    | 'Continue In Recording Dialog'
    | 'Approve Continue In Recording'
    | 'Force Continue In Recording'
    | 'Reject Continue In Recording'
    | 'iOS Recorder App Promotion Dialog'
    | 'Download On App Store'
    | 'Download On Play Store'
    | 'Open Mobile App Modal'
    | 'Start Recording'
    | 'Pause Recording'
    | 'Resume Recording'
    | 'Partial Recording Dialog'
    | 'Toggle Session Settings'
    | 'Upload Partial Recording'
    | 'Toggle Session End Reminders'
    | 'Toggle Sound Reminder'
    | 'Update Sound Reminder Value'
    | 'Toggle Max Recording Duration'
    | 'Update Max Recording Duration Value'
    | 'Background Recording Uploader'
    | 'Add Admin Privileges Confirm'
    | 'Add Admin Privileges Cancel'
    | 'Remove Admin Privileges Confirm'
    | 'Remove Admin Privileges Cancel'
    | 'Change Speakers'
    | 'Cancel Change Speakers'
    | 'Confirm Change Speakers'
    | 'Select New Speaker'
    | 'Sign Note'
    | 'Sign Note Cancel'
    | 'Sign Note Confirm'
    | 'Unlock Note'
    | 'Unlock Note Cancel'
    | 'Unlock Note Confirm'
    | 'Send To Client'
    | 'Send To Client Cancel'
    | 'Send To Client Confirm'
    | 'Revoke From Client Cancel'
    | 'Revoke From Client Confirm'
    | 'Try Golden Thread'
    | 'Treatment Plan'
    | 'Treatment Plan Onboarding'
    | 'Treatment Plan Onboarding Confirm'
    | 'Treatment Plan Onboarding Cancel'
    | 'Generate Treatment Plan'
    | 'Start Creating Treatment Plan'
    | 'Treatment Plan Generation Failed'
    | 'Regenerate Treatment Plan'
    | 'Select Treatment Plan Source Document'
    | 'Unselect Treatment Plan Source Document'
    | 'Cancel Treatment Plan Generation'
    | 'Open Treatment Plan'
    | 'Close Treatment Plan'
    | 'Edit Treatment Plan'
    | 'Cancel Treatment Plan Edit'
    | 'Save Treatment Plan'
    | 'Discard Treatment Plan'
    | 'Discard Treatment Plan Changes'
    | 'Treatment Plan Select Field Used'
    | 'Open Treatment Plan Menu'
    | 'Diagnoses Selection Changed'
    | 'Diagnoses Searched'
    | 'Modalities Selection Changed'
    | 'Modality Selection Changed'
    | 'Enter Case Snapshot Manually'
    | 'Generate Case Snapshot'
    | 'Add Goal'
    | 'Delete Goal'
    | 'Reorder Goals'
    | 'Add Treatment Area'
    | 'Delete Treatment Area'
    | 'Lift Interventions'
    | 'Add Objective'
    | 'Delete Objective'
    | 'Reorder Objectives'
    | 'Objective Stage Selection Changed'
    | 'Add Intervention'
    | 'Delete Intervention'
    | 'Review Transcript Settings'
    | 'Extension opened'
    | 'Open Install Extension'
    | 'Open Uninstall Extension'
    | 'Missing Unassigned Client Consent'
    | 'Consent Settings Changed'
    | 'Skip Unassigned Client Consent'
    | 'Disconnect Google SSO Cancel'
    | 'Disconnect Google SSO Confirm'
    | 'Referral Link Copy'
    | 'Join Referral Program'
    | 'Consent Confirmation'
    | 'Consent Confirmation Accepted'
    | 'Consent Article Link Clicked'
    | 'Mark Consent as Collected'
    | 'Consent Template Downloaded'
    | 'Consent Document Downloaded'
    | 'Consent Dialog Opened'
    | 'Editor Change Undone'
    | 'Editor Change Redone'
    | 'Smart Edit Opened'
    | 'Smart Edit Result Discarded'
    | 'Smart Edit Result Used'
    | 'Smart Edit Result Inserted After Selection'
    | 'Smart Edit Quotes Added'
    | 'Smart Edit Quote Deselected'
    | 'Smart Edit Quote Selected'
    | 'Smart Edit Command Run'
    | 'Smart Edit Command Retried'
    | 'Smart Edit Command Rerun'
    | 'Smart Edit Command Stopped'
    | 'Reprocess Session'
    | 'Reprocess Session Cancel'
    | 'Reprocess Session Document'
    | 'Reprocess Session Document Cancel'
    | 'Show Client Notes'
    | 'Show Add New Note Tab'
    | 'Generate Client Note'
    | 'More session options opened'
    | 'Session option clicked'
    | 'Reset Transcript'
    | 'Merge Utterances'
    | 'Split Utterance'
    | 'Toggle Speaker Mapping'
    | 'Create New Custom Template'
    | 'Submit New Custom Template Dialog'
    | 'Cancel New Custom Template Dialog'
    | 'Open Custom Template Section Picker'
    | 'Open Text To Note Dialog'
    | 'Close Text To Note Dialog'
    | 'Generate Text To Note'
    | 'Regenerate Text To Note Session'
    | 'Regenerate Session Cancel'
    | 'Text To Note Dialog Opened'
    | 'Open Select Custom Template Dialog'
    | 'Open Text To Note Session Summary'
    | 'Session Overview'
    | 'Delete Session Document'
    | 'Delete Session Document Cancel'
    | 'Change Recording Type'
    | 'Failed Payment Banner'
    | 'Update Payment Details'
    | 'Cancel Subscription'
    | 'Cancel Subscription Dialog'
    | 'Cancellation Flow Pause'
    | 'Cancellation Flow Cancel'
    | 'Cancellation Flow Survey Continue'
    | 'Cancellation Flow Contact Agent'
    | 'Cancellation Flow No Thanks'
    | 'Cancellation Flow Stay On Paid Plan'
    | 'Cancellation Flow Continue To Portal'
    | 'Cancellation Flow Dismiss'
    | 'Undo Cancellation'
    | 'Move Provider'
    | 'Update user contact information'
    | 'Remove SSO'
    | 'Calendar Loaded'
    | 'Calendar View Type'
    | 'Calendar Weekend Visibility'
    | 'Calendar Sessions Visibility'
    | 'Calendar Row Settings Opened'
    | 'Toggle Provider Calendar'
    | 'Reschedule Calendar Event'
    | 'Disconnect External Calendar'
    | 'Connect External Calendar'
    | 'Permanently Close Calendar Prompt'
    | 'Add New Calendar'
    | 'Regenerate Broken Session Document'
    | 'Open Help'
    | 'Open Help Video'
    | 'Open Help Panel'
    | 'Close Help Panel'
    | 'Next Tutorial Step'
    | 'Previous Tutorial Step'
    | 'Open Help Tutorial'
    | 'Close Help Tutorial'
    | 'Note Templates Search Query Changed'
    | 'Note Templates Filter Changed'
    | 'Note Template Card Redirected'
    | 'Note Template Card Used'
    | 'Note Template Preview Opened'
    | 'Note Template Preview Closed'
    | 'Note Template Set As Default'
    | 'Note Template Duplicated'
    | 'Note Template Deleted'
    | 'Note Template Delete Canceled'
    | 'Note Template Delete Confirmed'
    | 'Note Template Edited'
    | 'Note Template Updated'
    | 'Note Template Created'
    | 'Note Template Section Preview Regenerated'
    | 'Note Template Section Previewed'
    | 'Note Template Section Edited'
    | 'Note Template Section Settings Opened'
    | 'Note Template Section Reordered'
    | 'Note Template Section Title Changed'
    | 'Note Template Section Removed'
    | 'Note Template Section Picked'
    | 'Note Template Section Tag Selected'
    | 'New Template Menu Opened'
    | 'Extract Template From PDF'
    | 'Choose Default Template'
    | 'Convert a PDF'
    | 'Explore and Edit'
    | 'Dismiss PDF Banner'
    | 'Templates Page'
    | 'New Session'
    | 'Home Navigation'
    | 'Sessions Navigation'
    | 'Clients Navigation'
    | 'Inbox Navigation'
    | 'Calendar Navigation'
    | 'Billing Navigation'
    | 'Training Navigation'
    | 'Templates Navigation'
    | 'Note Audit Navigation'
    | 'Practice Settings Navigation'
    | 'My Profile Navigation'
    | 'Note Settings Navigation'
    | 'Uphealer Link Navigation'
    | 'Integration Navigation'
    | 'Calendar Settings Navigation'
    | 'Services Navigation'
    | 'Referral Program Navigation'
    | 'Consent Collection Navigation'
    | 'Compliance Checker Navigation'
    | 'Team Members Navigation'
    | 'Sessions Overview Navigation'
    | 'Client Reminder Navigation'
    | 'Plan And Billing Navigation'
    | 'More Navigation'
    | 'Customize Sidebar'
    | 'Switch to UphealOS'
    | 'Switch to Upheal Notes'
    | 'Switch to UphealOS Confirm'
    | 'Switch to UphealOS Cancel'
    | 'Switch to Upheal Notes Confirm'
    | 'Switch to Upheal Notes Cancel'
    | 'Change Experience Mode'
    | 'Experience Mode Navigation'
    | 'Open Experience Mode Selector'
    | 'Dictation Started'
    | 'Dictation Stopped'
    | 'Dictation Discarded'
    | 'Dictation Reached Max Allowed Time'
    | 'Open Device Menu'
    | 'Toggle Device'
    | 'Reminder Toggle Enabled'
    | 'Reminder Toggle Disabled'
    | 'Add Address'
    | 'Edit Address'
    | 'Delete Address'
    | 'Set Address As Primary'
    | 'Add Appointment Type'
    | 'Edit Appointment Type'
    | 'Delete Appointment Type'
    | 'Save Appointment Type Changes'
    | 'Copy Self Scheduling Link'
    | 'Open Self Scheduling Page'
    | 'Toggle Self Scheduling'
    | 'Permanently Close Appointment Types Prompt'
    | 'Select Appointment type'
    | 'Manage Appointment types'
    | 'Sign Out Self Scheduling'
    | 'Sign In Self Scheduling'
    | 'Confirm Self Scheduling'
    | 'Self Scheduling Slot Selected'
    | 'Self Scheduling Slot Reverted'
    | 'Self Scheduling Booking Confirmed'
    | 'Self Scheduling Sign In Not Authorised'
    | 'Copy Section to Clipboard'
    | 'Smart Fill'
    | 'Smart Fill Promo'
    | 'Smart Fill Promo CTA Clicked'
    | 'Smart Fill Promo Dismissed'
    | 'Delete Treatment Plan Clicked'
    | 'Delete Treatment Plan'
    | 'Delete Treatment Plan Cancel'
    | 'Treatment Plan Accordion Toggled'
    | 'Practice Setup Confirm'
    | 'Verify email code'
    | 'Continue with email'
    | 'Client Logout'
    | 'Go Back'
    | 'Treatment Plan V2 Start Page'
    | 'Treatment Plan V2 Error Page'
    | 'Treatment Plan V2 Suggested Sessions Page'
    | 'Treatment Plan V2 Case Snapshot Loading Page'
    | 'Treatment Plan V2 Case Snapshot Page'
    | 'Treatment Plan V2 Treatment Plan Loading Page'
    | 'Treatment Plan V2 Treatment Plan View Page'
    | 'Treatment Plan V2 Treatment Plan Edit Page'
    | 'Expand Treatment Summary'
    | 'Collapse Treatment Summary'
    | 'Change Session Video Call Tab'
    | 'Schedule session'
    | 'Note Audit Visited'
    | 'Note Audit File Uploaded'
    | 'Note Audit File Count'
    | 'Note Audit File Upload Retry'
    | 'Note Audit File Upload Delete'
    | 'Note Audit Started'
    | 'Note Audit Failed'
    | 'Note Audit Finished'
    | 'Note Audit Cancelled'
    | 'Note Audit Abandoned'
    | 'Note Audit Processing Duration'
    | 'Note Template Extract Visited'
    | 'Note Template Extract File Uploaded'
    | 'Note Template Extract Started'
    | 'Note Template Extract Processing Duration'
    | 'Note Template Extract Finished'
    | 'Note Template Extract Failed'
    | 'Note Template Extract Abandoned'
    | 'Timezone Set'
    | 'Timezone Updated'
    | 'Internal Audit Run'
    | 'Internal Audit Resolve Criterion'
    | 'Internal Audit Resolve Criterion On Note'
    | 'Internal Audit Unresolve Criterion'
    | 'Internal Audit Fix Criterion'
    | 'Internal Audit Changes Prompt'
    | 'Internal Audit Compliance Settings'
    | 'Internal Audit Dismiss Rerun'
    | 'Automatic Compliance Check Toggled'
    | 'Automatic Compliance Check Delay Changed'
    | 'Internal Audit Email Digest Toggled'
    | 'Internal Audit Email Digest Time Changed'
    | 'Internal Audit Open Drawer'
    | 'Primary Article'
    | 'Secondary Article'
    | 'Resource Link'
    | 'Open All Sessions'
    | 'Article Link'
    | 'Article CTA'
    | 'WatchTour'
    | 'Collapse Section'
    | 'Expand Section'
    | 'Connect Calendar'
    | 'Time Off Event Created'
    | 'Change Article Tab'
    | 'Client Billing (Business Details) Navigation'
    | 'Client Billing (Billing Documents) Navigation'
    | 'Practice Forms'
    | 'Practice Forms Add Form To Packet'
    | 'Practice Forms Remove Form From Packet'
    | 'Practice Forms Page'
    | 'Practice Forms Form Templates Button'
    | 'Practice Forms Packet Templates Button'
    | 'Practice Forms Create New Form'
    | 'Practice Forms Update Reminders'
    | 'Practice Forms Send Manual Reminder'
    | 'Practice Forms New Form'
    | 'Practice Forms New Form Dropdown'
    | 'Practice Forms New Packet Dropdown'
    | 'Practice Forms New Form Option Selected'
    | 'Practice Forms New Packet Option Selected'
    | 'Practice Forms Form Preview'
    | 'Practice Forms Packet Preview'
    | 'Practice Forms Edit Form'
    | 'Practice Forms Edit Packet'
    | 'Practice Forms Duplicate Form'
    | 'Practice Forms Delete Form'
    | 'Practice Forms Delete Packet'
    | 'Practice Forms Save Reminders'
    | 'Practice Forms New Reminder Added'
    | 'Practice Forms Reminder Removed'
    | 'Practice Forms Reminder Previewed'
    | 'Practice Forms Reminder Trigger Changed'
    | 'Practice Forms Reminder Timing Changed'
    | 'Practice Forms Send Forms Opened'
    | 'Practice Forms Send Packets Opened'
    | 'Practice Forms Send Forms Triggered'
    | 'Practice Forms Send Packets Triggered'
    | 'Practice Forms Send Forms Selected'
    | 'Practice Forms Form Template Previewed'
    | 'Practice Forms Form Template Used'
    | 'Practice Forms Packet Template Used'
    | 'Practice Forms Packet Template Previewed'
    | 'Practice Forms Search Forms'
    | 'Practice Forms Search Packets'
    | 'Practice Forms Search Templates'
    | 'Practice Forms Search Packet Templates'
    | 'Practice Forms Tab Selected'
    | 'Practice Forms Type Tab Selected'
    | 'Practice Forms View Form Templates'
    | 'Practice Forms View Packet Templates'
    | 'Practice Forms Dismiss Templates Banner'
    | 'Practice Forms Canvas Page'
    | 'Practice Forms Canvas Settings Clicked'
    | 'Practice Forms Canvas Form Type Changed'
    | 'Practice Forms Canvas Form Category Changed'
    | 'Practice Forms Canvas Form Label Deleted'
    | 'Practice Forms Canvas Form Settings Saved'
    | 'Practice Forms Canvas Closed'
    | 'Practice Forms Canvas Preview'
    | 'Practice Forms Canvas New Form Created'
    | 'Practice Forms Canvas Form Updated'
    | 'Practice Forms Canvas Block Used'
    | 'Practice Forms Canvas Block Duplicated'
    | 'Practice Forms Canvas Block Option Added'
    | 'Practice Forms Canvas Block Option Removed'
    | 'Practice Forms Canvas Block Deleted'
    | 'Practice Forms Canvas Block Required'
    | 'Practice Forms Canvas Block Scored'
    | 'Practice Forms Canvas Block Heading Level Changed'
    | 'Practice Forms Canvas Block Type Changed'
    | 'Practice Forms Canvas Likert Scale Column Added'
    | 'Practice Forms Canvas Likert Scale Column Removed'
    | 'Practice Forms Canvas Likert Scale Question Added'
    | 'Practice Forms Canvas Likert Scale Question Removed'
    | 'Practice Forms Canvas Module Used'
    | 'Practice Forms Canvas Reorder Used'
    | 'Practice Forms Canvas Input Linked To Client Detail'
    | 'Practice Forms Create New Packet'
    | 'Practice Forms Dismiss Legal Banner'
    | 'Practice Forms New Packet Created'
    | 'Practice Forms Packets Builder Closed'
    | 'Practice Forms Packet Updated'
    | 'Practice Forms New Template'
    | 'Practice Forms Template Preview'
    | 'Practice Forms Edit Template'
    | 'Practice Forms Duplicate Template'
    | 'Practice Forms Delete Template'
    | 'Practice Forms Canvas New Template Created'
    | 'Practice Forms Canvas Template Updated'
    | 'Practice Forms Template Canvas Closed'
    | 'Practice Forms AI Import'
    | 'Practice Forms Import Modal Opened'
    | 'Practice Forms Conversion Started'
    | 'Practice Forms Conversion Initiated'
    | 'Practice Forms Conversion Completed'
    | 'Practice Forms Conversion Failed'
    | 'Practice Forms Form Import Promo Banner Dismiss'
    | 'Client Detail Forms & Settings Navigation'
    | 'Client Detail Forms Tab Changed'
    | 'Client Detail Packets Tab Changed'
    | 'Client Detail Send Forms Opened'
    | 'Client Detail Send Forms Triggered'
    | 'Client Detail Send Forms Searched'
    | 'Client Detail Send Packets Searched'
    | 'Client Detail Send Forms Selected'
    | 'Client Detail Send Packets Selected'
    | 'Client Detail Form Viewed'
    | 'Client Detail Form Request Searched'
    | 'Client Detail Form Downloaded As PDF'
    | 'Client Detail Form Request Revoked'
    | 'Client Portal Signed In'
    | 'Client Portal Log Out'
    | 'Client Portal Requested Magic Link'
    | 'Client Portal Viewed Form'
    | 'Client Portal Submit Form'
    | 'Client Portal Next Document'
    | 'Client Portal Selected Form'
    | 'Client Portal Send Invite'
    | 'Chart Notes New Note Created'
    | 'Chart Notes Note Updated'
    | 'Chart Notes Delete Note'
    | 'Chart Notes Type Tab Selected'
    | 'Chart Notes Sort By Date'
    | 'Chart Notes Add New Note'
    | 'Chart Notes Note Type Selected'
    | 'Chart Notes Note Editor Page Opened'
    | 'Chart Notes Note Detail Page Opened'
    | 'Chart Notes Close Editor'
    | 'Chart Notes Close Note Detail'
    | 'Chart Notes Edit This Note'
    | 'Chart Notes Download Note PDF'
    | 'Chart Notes Changed Page'
    | 'Chart Notes Changed Results Per Page'
    | 'Chart Files Upload File'
    | 'Chart Files Confirm Upload'
    | 'Chart Files Changed Page'
    | 'Chart Files Changed Results Per Page'
    | 'Open Import From EHR'
    | 'Start Migration From SimplePractice'
    | 'Add Clients From Another EHR'
    | 'Try Upheal OS Later'
    | 'Try Upheal OS Now'
    | 'Unsupported EHR Import'
    | 'Learn More'
    | 'Dismiss Banner'
    | 'Chart Migration Page Viewed'
    | 'Chart Migration Start SimplePractice'
    | 'Chart Migration Start Other EHR'
    | 'Chart Migration Other EHR Back'
    | 'Chart Migration Instructions Viewed'
    | 'Chart Migration Small Screen Blocker Viewed'
    | 'Chart Migration Upload Step Viewed'
    | 'Chart Migration File Selected'
    | 'Chart Migration File Upload Started'
    | 'Chart Migration File Upload Success'
    | 'Chart Migration File Upload Error'
    | 'Chart Migration File Processing Validation Error'
    | 'Chart Migration File Processing Error'
    | 'Chart Migration Next Step'
    | 'Chart Migration Skip Step'
    | 'Chart Migration Previous Step'
    | 'Chart Migration Confirm Import'
    | 'Chart Migration Reset'
    | 'Chart Migration Complete'
    | 'Chart Migration Initial Screen Viewed'
    | 'Chart Migration FAQ Viewed'
    | 'Chart Migration Transfer Type Selected'
    | 'Chart Migration Reuse Import Selected'
    | 'Chart Migration Import Process Created'
    | 'Chart Migration Upload Step Entered'
    | 'Chart Migration Processing Uploads Started'
    | 'Chart Migration Processing Uploads Complete'
    | 'Chart Migration Map Providers Step Viewed'
    | 'Chart Migration Provider Mapped'
    | 'Chart Migration Provider Created'
    | 'Chart Migration Building Clients Started'
    | 'Chart Migration Map Clients Step Viewed'
    | 'Chart Migration Client Action Selected'
    | 'Chart Migration Client Bulk Action'
    | 'Chart Migration Stage Complete Screen Viewed'
    | 'Chart Migration Review Step Viewed'
    | 'Chart Migration Review Detail Expanded'
    | 'Chart Migration Import Started'
    | 'Chart Migration Import Summary Viewed'
    | 'Chart Migration Failed Items Downloaded'
    | 'Chart Migration Go Home Clicked'
    | 'Chart Migration Merging Strategy'
    | 'Chart Migration Merging Strategy Bulk Update'
    | 'Chart Migration Review Confirmed'
    | 'Chart Migration Tab Changed'
    | 'Chart Migration Review Detail Page Viewed'
    | 'Chart Migration Client Mapping Confirmed'
    | 'Chart Migration Intercom Opened'
    | 'Chart Migration Step Duration'
    | 'Client Billing Gate Viewed'
    | 'Client Billing Gate Unsupported Country Viewed'
    | 'Stripe Setup Started'
    | 'Stripe Onboarding Link Created'
    | 'Stripe Dashboard Opened'
    | 'Stripe Status Refreshed'
    | 'Stripe Setup Completed'
    | 'Stripe Setup Failed'
    | 'Stripe Setup Duration'
    | 'Session Billing Drawer Opened'
    | 'Invoice Creation Started'
    | 'Invoice Created'
    | 'Invoice Creation Failed'
    | 'Invoice Charge Card Clicked'
    | 'Invoice Charged'
    | 'Invoice Marked As Paid'
    | 'Invoice Voided'
    | 'Credit Note Created'
    | 'Credit Note Creation Failed'
    | 'Payment Settings Drawer Opened'
    | 'New Superbill Drawer Opened'
    | 'New Invoice Drawer Opened'
    | 'Billing Drawer Opened'
    | 'Open Billing Period Dialog'
    | 'Navigate to Payment Settings'
    | 'Fill Billing Data'
    | 'Setup Online Payments'
    | 'Generate invite link'
    | 'Save SAML Configuration'
    | 'Cancel SAML Configuration'
    | 'Test SAML Connection'
    | 'Test SAML Login'
    | 'Delete SAML Configuration'
    | 'Download SAML Metadata'
    | 'Configure SAML SSO'
    | 'Disable SAML SSO'
    | 'Edit SAML Configuration'
    | 'Enable SAML SSO'
    | 'Enable SAML SSO Confirm'
    | 'Enable SAML SSO Cancel'
    | 'Enable Google SSO'
    | 'Disable Google SSO'
    | 'Disable Google SSO Confirm'
    | 'Disable Google SSO Cancel'
    | 'Disable SAML SSO Confirm'
    | 'Disable SAML SSO Cancel'
    | 'Delete SAML Configuration Confirm'
    | 'Delete SAML Configuration Cancel'
    | 'Switch SSO from Google to Google Confirm'
    | 'Switch SSO from Google to SAML Confirm'
    | 'Switch SSO from SAML to Google Confirm'
    | 'Switch SSO from SAML to SAML Confirm'
    | 'Switch SSO from Google to Google Cancel'
    | 'Switch SSO from Google to SAML Cancel'
    | 'Switch SSO from SAML to Google Cancel'
    | 'Switch SSO from SAML to SAML Cancel'
    | 'Open AI Chat'
    | 'Close AI Chat'
    | 'Clear AI Chat History'
    | 'Clear AI Chat History Confirm'
    | 'Submit AI Chat Question'
    | 'Stop AI Chat Generation'
    | 'Open AI Chat Client Selector'
    | 'Select AI Chat Client'
    | 'Open AI Chat Session Selector'
    | 'Select AI Chat Session'
    | 'Select AI Chat Category'
    | 'Select AI Chat Predefined Question'
    | 'Select AI Chat Follow-up Question'
    | 'Dismiss AI Chat Follow-up Questions'
    | 'Toggle AI Chat Sources'
    | 'Copy AI Chat Message'
    | 'Toggle AI Chat Suggestions'
    | 'AI Chat See Plans'
    | 'Toggle External Calendar Availability'
    | 'Select Inbox Conversation'
    | 'Open Compose Message Dialog'
    | 'Close Compose Message Dialog'
    | 'Send Compose Message'
    | 'Unsupported Currency Detected'
    | 'Send Inbox Message'
    | 'Google Calendar Migration Modal'
    | 'Start Google Calendar Migration'
    | 'Complete Google Calendar Migration'
    | 'Close Whats New Modal'
    | 'Open Customization Modal'
    | 'Close Customization Modal'
    | 'Home'
    | 'Calendar'
    | 'Sessions'
    | 'Clients'
    | 'Payments'
    | 'Practice forms'
    | 'Note templates'
    | 'Inbox'
    | 'Tools'
    | 'Client List Import Page Viewed'
    | 'Client List Import Submit'
    | 'Client List Import CSV Uploaded'
    | 'Client List Import Step Changed'
    | 'Client List Import Client Resolved'
    | 'Client List Import Contract Type Changed'
    | 'Client List Import Provider Changed'
    | 'Client List Import Assign All Providers'
    | 'Client List Import Couple Assigned'
    | 'Client List Import Cancelled'
    | 'Client List Import Resolve Duplicates Viewed'
    | 'Client List Import Map Contracts Viewed'
    | 'Client List Import Extract Individual From Menu'
    | 'Open Import CSV'
    | 'Open Add Bulk'
    | SignUpUiEventName;

export type UiEventAction =
    | 'View'
    | 'Click'
    | 'Doubleclick'
    | 'Keyboard'
    | 'Select'
    | 'Input'
    | 'Error'
    | 'DragAndDrop'
    | 'Close'
    | 'System'
    | 'Unknown';
